import {
	applyCardMove,
	type GenericKanbanCard,
	KanbanBoard,
	type KanbanCardMove,
} from "@userabuser/app-ui-kanban";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal } from "../../components/Modal";
import { type BoardColumn, createJob, getJobs, updateJob, uploadAttachment } from "../../lib/api";
import { NewJobForm, type JobFormPageKey } from "./components/NewJobForm";
import { NEW_JOB_FORM_HELP } from "./components/newJobFormHelp";
import styles from "./KanbanBoardView.module.css";
import { costSummary } from "../costing/costing";
import {
	STAGE_MISMATCH_LABELS,
	formatApproximateDate,
	isWaiting,
	stageMismatch,
} from "./lib/schedule";
import type { IntendedAction } from "./types";
import {
	formatPriceCents,
	type JobDraft,
	type JobRecord,
	jobHeadlineValueCents,
	jobRecordToDraft,
} from "./lib/jobForm";
import { formatMoneyCompact } from "./lib/money";
import { NewJobFormPrototype } from "./prototype/NewJobFormPrototype";
import {
	renderJobCardVariant,
	useJobCardVariant,
} from "./prototype/JobCardFacePrototype";

/** Fired on the window after a job is created, edited, or moved so listeners (the nav totals) can refresh. */
const JOBS_CHANGED_EVENT = "afs:jobs-changed";

/** A job card on the board. Extends the package's generic card with a title. */
interface JobCard extends GenericKanbanCard {
	title: string;
	status?: string;
	priority?: string;
	refExternal?: string;
	refInternal?: string;
	/** Service address shown beneath the card title. */
	propertyAddress: string;
	/** Client name shown as the card's last line; unset when no client. */
	clientName?: string;
	/** Internal price estimate in cents. */
	priceEstimate?: number;
	/** Number of scope line items captured on Page 2. */
	scopeItemCount?: number;
	/** Derived scope total including GST, in cents; unset when there are no items. */
	scopeTotalCents?: number;
	/** True when the job has an accepted quote (drives the In-progress warn gate). */
	hasAcceptedQuote?: boolean;
	/**
	 * Schedule Intent, flattened for the card face. `scheduleAction` is the raw
	 * internal value and is null for client-scoped callers, whose payload only
	 * carries the label — so the chip degrades to the published wording without
	 * a second code path, and the stage-mismatch marker (which is internal) is
	 * simply never computed for them.
	 */
	scheduleAction?: IntendedAction | null;
	scheduleLabel?: string;
	scheduleWhen?: string;
}

/**
 * Board card projection of a job record. Scope totals are derived here from
 * the line items (never stored), so the card always agrees with the costing
 * math used on the form's Page 2.
 */
function jobToCard(job: JobRecord): JobCard {
	const lineItems = job.scope?.lineItems ?? [];
  // console.log({job});
  
	return {
		id: job.id,
		columnId: job.columnId,
		title: job.title,
		status: job.status,
		priority: job.priority,
		refExternal: job.refExternal,
		refInternal: job.refInternal,
		propertyAddress: job.propertyAddress,
		clientName: job.clientName || undefined,
		priceEstimate: job.priceEstimate,
		scopeItemCount: lineItems.length,
		scopeTotalCents:
			lineItems.length > 0
				? costSummary(lineItems, job.scope?.taxRateBps).totalCents
				: undefined,
		hasAcceptedQuote: job.hasAcceptedQuote,
		scheduleAction: job.schedule?.action ?? null,
		scheduleLabel: job.schedule?.action ?? job.schedule?.clientLabel ?? "",
		scheduleWhen: formatApproximateDate(
			job.schedule?.intendedOn ?? "",
			job.schedule?.precision ?? "",
		),
	};
}

/**
 * The card's Schedule Intent: what we intend to do next and roughly when.
 *
 * The mismatch marker is computed here rather than in `jobToCard` so it stays
 * correct mid-drag — dropping a waiting card into Hold resolves it without a
 * refetch. It needs the raw action, so client-scoped viewers never see it: the
 * disagreement between a plan and a board column is an internal concern
 * (DEC-JS-004).
 */
function ScheduleChip({ card }: { card: JobCard }) {
	const text = [card.scheduleLabel, card.scheduleWhen].filter(Boolean).join(" · ");
	if (!text) return null;
	const mismatch = stageMismatch(card.scheduleAction ?? "", card.columnId);
	const waiting = isWaiting(card.scheduleAction ?? "");

	return (
		<span className={styles.cardScheduleRow}>
			<span className={waiting ? styles.cardScheduleWaiting : styles.cardSchedule}>
				{waiting ? "⏳" : "→"} {text}
			</span>
			{mismatch && (
				<span className={styles.cardScheduleFlag} title={STAGE_MISMATCH_LABELS[mismatch]}>
					!
				</span>
			)}
		</span>
	);
}

interface KanbanBoardViewProps {
	/** Column definitions supplied by the backend. */
	columns: readonly BoardColumn[];
}

const EMPTY_CARDS: JobCard[] = [];

// Briefly coalesce consecutive completed moves of the same card.
const MOVE_DEBOUNCE_MS = 250;

// Only Drafts accepts brand-new jobs, so only it renders the "+" affordance.
// The package couples this set to hydration too, so hydration commits through
// the controlled `cards` state instead of the mint-validating hydrate handle.
const MINTABLE_COLUMN_IDS: readonly string[] = ["drafts"];

// PROTOTYPE — dev builds render the ?variant= switcher around the form; production
// always gets the real one. Remove with src/features/jobs/prototype once a layout wins.
const JobFormHost = import.meta.env.DEV ? NewJobFormPrototype : NewJobForm;

// The modal's page workflow per mode. A draft job has nothing to discuss and
// no history yet, so Comments and Activity exist only once the card is
// persisted (job-collaboration / card-activity epics).
const JOB_FORM_PAGES_CREATE: readonly JobFormPageKey[] = [
	"details",
	"scope",
	"tasks",
	"schedule",
	"attachments",
];
const JOB_FORM_PAGES_EDIT: readonly JobFormPageKey[] = [
  ...JOB_FORM_PAGES_CREATE,
  "comments",
  "activity",
];

type ModalState = {
	mode: "create" | "edit";
	columnId: string;
	cardId: string;
	jobStatus?: string;
} | null;

type MoveConfirmationState = {
	move: KanbanCardMove;
	jobTitle: string;
	rollback: { columnId: string; position: number };
};

/**
 * Controlled wrapper around @userabuser/app-ui-kanban.
 *
 * The board owns no card state; this component is the source of truth. Cards are
 * hydrated from the backend on mount and every create/edit is persisted via the
 * jobs API. The Drafts column's "+" opens the New Job form in create mode, and
 * activating a card opens it in edit mode; the full job draft is kept per card
 * id so the form can be pre-filled on edit.
 *
 * @param {KanbanBoardViewProps} props - Board column configuration.
 */
export function KanbanBoardView({ columns }: KanbanBoardViewProps) {
	const [searchParams, setSearchParams] = useSearchParams();
	const requestedJobId = searchParams.get("job");
	// PROTOTYPE — card-face variants, dev only. Remove with src/features/jobs/prototype.
	const cardVariant = useJobCardVariant();
	const [cards, setCards] = useState<JobCard[]>(EMPTY_CARDS);
	const [hasLoadedJobs, setHasLoadedJobs] = useState(false);
	const [moveConfirmation, setMoveConfirmation] =
		useState<MoveConfirmationState | null>(null);
	const [modal, setModalState] = useState<ModalState>(null);
	const modalRef = useRef<ModalState>(null);
	const setModal = useCallback((next: ModalState): void => {
		modalRef.current = next;
		setModalState(next);
	}, []);

	// Mirror of `cards`, so a handler can read the current list without a state
	// updater. Recording the persisted position inside `setCards` made that
	// updater impure, and StrictMode runs it twice.
	const cardsRef = useRef<JobCard[]>(EMPTY_CARDS);
	const commitCards = useCallback((next: JobCard[]): void => {
		cardsRef.current = next;
		setCards(next);
	}, []);
	// Full job data keyed by card id, so the form can be pre-filled on edit.
	const draftsById = useRef<Map<string, JobDraft>>(new Map());

	// Last { columnId, position } confirmed persisted for each card, so a move
	// only triggers a write when the column or the within-column order changes.
	const lastPersisted = useRef<
		Map<string, { columnId: string; position: number }>
	>(new Map());

	// Per-card debounce timers that coalesce the many move events a single drag
	// emits into one trailing PATCH reflecting the final column and order.
	const moveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
		new Map(),
	);
	// The board reports controlled position changes during drag-over. Keep only
	// the latest move and the drag-start position, then act when the drag ends.
	const activeMove = useRef<MoveConfirmationState | null>(null);
	const confirmationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Index of a card within its column in the given card array (0-based).
	const columnIndexOf = useCallback(
		(list: readonly JobCard[], cardId: string): number => {
			const card = list.find((entry) => entry.id === cardId);
			if (!card) return 0;
			return list
				.filter((entry) => entry.columnId === card.columnId)
				.findIndex((entry) => entry.id === cardId);
		},
		[],
	);

	useEffect(() => {
		let cancelled = false;
		getJobs()
			.then(({ jobs }) => {
				if (cancelled) return;
				const hydrated: JobCard[] = jobs.map(jobToCard);
				const drafts = new Map<string, JobDraft>();
				const persisted = new Map<
					string,
					{ columnId: string; position: number }
				>();
				for (const job of jobs) {
					drafts.set(job.id, jobRecordToDraft(job));
					persisted.set(job.id, {
						columnId: job.columnId,
						position: job.position,
					});
				}
				commitCards(hydrated);
				draftsById.current = drafts;
				lastPersisted.current = persisted;
				setHasLoadedJobs(true);
			})
			.catch((error) => {
				if (cancelled) return;
				console.error("Failed to load jobs", error);
				setHasLoadedJobs(true);
			});
		return () => {
			cancelled = true;
		};
	}, [commitCards]);

	useEffect(() => {
		if (!hasLoadedJobs || !requestedJobId) return;
		const card = cards.find((candidate) => candidate.id === requestedJobId);
		if (card) {
			setModal({
				mode: "edit",
				columnId: card.columnId,
				cardId: card.id,
				jobStatus: card.status,
			});
		}
		setSearchParams(
			(current) => {
				const next = new URLSearchParams(current);
				next.delete("job");
				return next;
			},
			{ replace: true },
		);
	}, [cards, hasLoadedJobs, requestedJobId, setModal, setSearchParams]);

	const cancelScheduledMove = useCallback((cardId: string): void => {
		const timer = moveTimers.current.get(cardId);
		if (timer) clearTimeout(timer);
		moveTimers.current.delete(cardId);
	}, []);

	const persistCardMove = useCallback(
		(move: KanbanCardMove): void => {
			const { cardId, toColumnId, toIndex } = move;
			const previous = lastPersisted.current.get(cardId);
			if (
				previous &&
				previous.columnId === toColumnId &&
				previous.position === toIndex &&
				!moveTimers.current.has(cardId)
			) {
				return;
			}

			cancelScheduledMove(cardId);
			moveTimers.current.set(
				cardId,
				setTimeout(() => {
					moveTimers.current.delete(cardId);
					updateJob(cardId, { columnId: toColumnId, position: toIndex })
						.then(() => {
							lastPersisted.current.set(cardId, {
								columnId: toColumnId,
								position: toIndex,
							});
							// A move can change which value bucket (drafts/potential/realized) a job counts toward.
							window.dispatchEvent(new CustomEvent(JOBS_CHANGED_EVENT));
						})
						.catch((error) => {
							console.error("Failed to persist card move", error);
						});
				}, MOVE_DEBOUNCE_MS),
			);
		},
		[cancelScheduledMove],
	);

	useEffect(() => {
		const completeActiveMove = (): void => {
			const pending = activeMove.current;
			if (!pending) return;
			activeMove.current = null;
			confirmationTimer.current = setTimeout(() => {
				confirmationTimer.current = null;
				const card = cardsRef.current.find(
					(candidate) => candidate.id === pending.move.cardId,
				);
				const needsConfirmation =
					pending.move.toColumnId === "in-progress" &&
					pending.rollback.columnId !== "in-progress" &&
					!card?.hasAcceptedQuote &&
					draftsById.current.get(pending.move.cardId)?.requireQuoteApproval;
				if (needsConfirmation) {
					setMoveConfirmation(pending);
				} else {
					persistCardMove(pending.move);
				}
			}, 0);
		};
		const handleDragKeyUp = (event: KeyboardEvent): void => {
			if (event.key === "Enter" || event.key === " ") {
				completeActiveMove();
			}
		};

		window.addEventListener("pointerup", completeActiveMove);
		window.addEventListener("keyup", handleDragKeyUp);
		return () => {
			window.removeEventListener("pointerup", completeActiveMove);
			window.removeEventListener("keyup", handleDragKeyUp);
			if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
		};
	}, [persistCardMove]);

	const handleCardMove = useCallback(
		(move: KanbanCardMove): void => {
			const { cardId } = move;
			const before = cardsRef.current.find((entry) => entry.id === cardId);
			if (!before) return;
			const beforePosition = columnIndexOf(cardsRef.current, cardId);
			commitCards(applyCardMove(cardsRef.current, move));

			const previous =
				activeMove.current?.move.cardId === cardId
					? activeMove.current
					: null;
			activeMove.current = {
				move,
				jobTitle: before.title,
				rollback: previous?.rollback ?? {
					columnId: before.columnId,
					position: beforePosition,
				},
			};
		},
		[columnIndexOf, commitCards],
	);

	const handleDragCancel = useCallback(
		(previousCards: readonly JobCard[]): void => {
			const cardId = activeMove.current?.move.cardId;
			if (cardId) cancelScheduledMove(cardId);
			if (confirmationTimer.current) {
				clearTimeout(confirmationTimer.current);
				confirmationTimer.current = null;
			}
			activeMove.current = null;
			commitCards([...previousCards]);
		},
		[cancelScheduledMove, commitCards],
	);

	const cancelMoveConfirmation = useCallback((): void => {
		if (!moveConfirmation) return;
		cancelScheduledMove(moveConfirmation.move.cardId);
		commitCards(
			applyCardMove(cardsRef.current, {
				cardId: moveConfirmation.move.cardId,
				toColumnId: moveConfirmation.rollback.columnId,
				toIndex: moveConfirmation.rollback.position,
			}),
		);
		setMoveConfirmation(null);
	}, [cancelScheduledMove, commitCards, moveConfirmation]);

	const confirmMove = useCallback((): void => {
		if (!moveConfirmation) return;
		persistCardMove(moveConfirmation.move);
		setMoveConfirmation(null);
	}, [moveConfirmation, persistCardMove]);

	const handleCardCreate = useCallback(
		(created: JobCard[]): void => {
			const card = created[0];
			if (!card) return;
			setModal({
				mode: "create",
				columnId: card.columnId,
				cardId: card.id,
				jobStatus: card.columnId,
			});
		},
		[setModal],
	);

	const handleCardActivate = useCallback(
		(card: JobCard): void => {
			setModal({
				mode: "edit",
				columnId: card.columnId,
				cardId: card.id,
				jobStatus: card.status,
			});
		},
		[setModal],
	);

	/** Keeps the board's accepted-quote flag fresh after an in-modal acceptance. */
	const markQuoteAccepted = useCallback(
		(cardId: string): void => {
			commitCards(
				cardsRef.current.map((card) =>
					card.id === cardId ? { ...card, hasAcceptedQuote: true } : card,
				),
			);
		},
		[commitCards],
	);

	/**
	 * Uploads the staged files after the job row exists. Every failure is
	 * reported (the job itself is already saved); a missing file can always be
	 * re-added by editing the card.
	 */
	const uploadStagedFiles = async (jobId: string, files: Map<string, File>): Promise<void> => {
		const failures: string[] = [];
		for (const file of files.values()) {
			try {
				await uploadAttachment(jobId, file);
			} catch {
				failures.push(file.name);
			}
		}
		if (failures.length > 0) {
			throw new Error(`Saved, but ${failures.length} file upload(s) failed: ${failures.join(", ")}`);
		}
	};

	const handleJobSubmit = useCallback(
		async (draft: JobDraft, files: Map<string, File>): Promise<void> => {
			const current = modalRef.current;
			if (!current) return;
			if (current.mode === "create") {
				const { job } = await createJob({
					id: current.cardId,
					columnId: current.columnId,
					draft,
				});
				await uploadStagedFiles(job.id, files);
				const next = [...cardsRef.current, jobToCard(job)];
				commitCards(next);
				lastPersisted.current.set(job.id, {
					columnId: job.columnId,
					position: columnIndexOf(next, job.id),
				});
			} else {
				const { job } = await updateJob(current.cardId, { draft });
				await uploadStagedFiles(current.cardId, files);
				const next = cardsRef.current.map((card) =>
					card.id === job.id ? { ...card, ...jobToCard(job) } : card,
				);
				commitCards(next);
				lastPersisted.current.set(job.id, {
					columnId: job.columnId,
					position: columnIndexOf(next, job.id),
				});
			}
			draftsById.current.set(current.cardId, draft);
			setModal(null);
			window.dispatchEvent(new CustomEvent(JOBS_CHANGED_EVENT));
		},
		[columnIndexOf, commitCards, setModal],
	);

	const closeModal = useCallback((): void => setModal(null), [setModal]);

	/**
	 * Renders the job card face: title, service address beneath it, the
	 * references and headline value, and the client as the last line (0013):
	 * the derived Scope total when line items exist, otherwise the
	 * manual price estimate when provided — one value line, never both.
	 */
	function renderJobCard(card: JobCard) {
    // console.log(card,Object.fromEntries(cardsRef.current.map(obj => [obj.id, obj]))[card.id]);

		// PROTOTYPE — `?cardVariant=A|B|C|D` swaps the face in dev builds only.
		if (import.meta.env.DEV) {
			const prototypeFace = renderJobCardVariant(cardVariant, { card });
			if (prototypeFace) return prototypeFace;
		}

		return (
			<div className={styles.cardFace}>
				<strong className={styles.cardTitle}>{card.title}</strong>
				<span className={styles.cardAddress} title={card.propertyAddress}>
					{card.propertyAddress}
				</span>
				{(card.refInternal || card.refExternal) && (
					<div className={styles.cardRefs}>
						{card.refInternal ? (
							<span className={styles.cardRef}>#{card.refInternal}</span>
						) : null}
						{card.refExternal ? (
							<span className={styles.cardRef}>Ext: {card.refExternal}</span>
						) : null}
					</div>
				)}
				{card.scopeItemCount ? (
					<span className={styles.cardScope}>
						{card.scopeItemCount} item{card.scopeItemCount === 1 ? "" : "s"} ·{" "}
						{formatPriceCents(card.scopeTotalCents ?? 0)} incl. GST
					</span>
				) : card.priceEstimate ? (
					<span className={styles.cardPrice} title="Manual price estimate (no scope yet)">
						{formatPriceCents(card.priceEstimate)} est.
					</span>
				) : null}
				<ScheduleChip card={card} />
				{card.clientName ? (
					<span className={styles.cardClient}>{card.clientName}</span>
				) : null}
			</div>
		);
	}

	// Headline value per column, derived — never typed (0013): the Scope &
	// Costing total when the job has line items, else its price estimate.
	// Reads draftsById inside the memo: every mutation that changes a draft also
	// produces a new `cards` array in the same commit, and `draftsById` is always
	// assigned before the render that observes it (hydration, submit), so the
	// ref is in sync with the `cards` snapshot this memo is keyed on.
	const columnTotals = useMemo(() => {
		const totals: Record<string, number> = {};
		for (const card of cards) {
			const draft = draftsById.current.get(card.id);
			if (!draft) continue;
			totals[card.columnId] = (totals[card.columnId] ?? 0) + jobHeadlineValueCents(draft) / 100;
		}
		return totals;
	}, [cards]);

	const boardColumns = useMemo(
		() =>
			columns.map((column) => {
				const total = columnTotals[column.id] ?? 0;
				return {
					...column,
					headerExtra: total > 0 ? formatMoneyCompact(total) : undefined,
				};
			}),
		[columns, columnTotals],
	);

	const createCard = useCallback(
		(columnId: string): JobCard => ({
			id: `job-${crypto.randomUUID()}`,
			columnId,
			title: "New job",
			propertyAddress: "",
		}),
		[],
	);

	/**
	 * Puts the job's priority on the card's outer `.akb-card-wrapper` element
	 * (via the package's cardWrapperAttributes hook) so styling and scripting
	 * can target cards by priority without touching the card face markup.
	 */
	const cardWrapperAttributes = useCallback(
		(card: JobCard) => ({ "data-priority": card.priority }),
		[],
	);

	return (
		<section className={styles.root} aria-label="Jobs board">
			{/* <header className={styles.header}>
				<h1 className={styles.heading}>Jobs</h1>
				<p className={styles.subtitle}>
				Drag jobs across stages. Use the Drafts column&rsquo;s + to add a
				job, or open a card to edit it.
			</p>
		</header> */}
		<KanbanBoard
			columns={boardColumns}
			cards={cards}
			mintableColumnIds={MINTABLE_COLUMN_IDS}
				createCard={createCard}
				onCardMove={handleCardMove}
				onDragCancel={handleDragCancel}
				onCardCreate={handleCardCreate}
				onCardActivate={handleCardActivate}
				cardWrapperAttributes={cardWrapperAttributes}
				renderCardContent={renderJobCard}
				label="Jobs board"
			/>
			{moveConfirmation && (
				<Modal
					title="Move job to In-progress?"
					subtitle={moveConfirmation.jobTitle}
					size="sm"
					onClose={cancelMoveConfirmation}
					footer={
						<>
							<button
								type="button"
								className={styles.secondaryButton}
								onClick={cancelMoveConfirmation}
							>
								Cancel move
							</button>
							<button
								type="button"
								className={styles.primaryButton}
								onClick={confirmMove}
							>
								Move anyway
							</button>
						</>
					}
				>
					<p className={styles.confirmationCopy}>
						Quote approval is required for this job, but no quote has been
						accepted. You can move it anyway, or cancel to return it to its
						previous stage.
					</p>
				</Modal>
			)}
			{modal && (
				<JobFormHost
					key={`${modal.mode}-${modal.cardId}`}
					title={modal.mode === "create" ? "New job" : "Edit job"}
					initialValue={
						modal.mode === "edit"
							? draftsById.current.get(modal.cardId)
							: undefined
					}
					submitLabel={
						modal.mode === "create" ? "Create job" : "Save changes"
					}
					onSubmit={handleJobSubmit}
					onCancel={closeModal}
					pages={modal.mode === "edit" ? JOB_FORM_PAGES_EDIT : JOB_FORM_PAGES_CREATE}
					jobId={modal.mode === "edit" ? modal.cardId : undefined}
					jobStatus={modal.jobStatus}
					onQuoteAccepted={() => markQuoteAccepted(modal.cardId)}
					help={NEW_JOB_FORM_HELP}
				/>
			)}
			{/* <JobCardFaceSwitcher current={cardVariant} /> */}
		</section>
	);
}
